/**
 * Second-screen bridge.
 *
 * Sales reps run the dashboard on two screens: the Sales Inbox on one, the
 * orders/offers board on the other. This bridge lets a window *push* a route
 * to a single, explicitly-marked "target" window of the same origin, so e.g.
 * pressing a shortcut on a sales item filters the board on the second screen —
 * without stealing focus from the inbox and without disturbing any other open
 * dashboard window.
 *
 * Targeting: exactly one tab is the target at a time. The rep marks a tab via
 * the header toggle (markAsTarget); a pushed route is applied ONLY by the tab
 * whose id matches the stored target. Tabs that aren't the target ignore the
 * push. With no target marked, a push lands nowhere (the sender hints at this).
 *   - per-tab id  -> sessionStorage (stable across reloads of that tab)
 *   - target id   -> localStorage   (shared, so every tab knows who the target is)
 *
 * Transport: BroadcastChannel when available (a posted message is delivered to
 * every same-origin window EXCEPT the sender). Where BroadcastChannel is
 * missing we fall back to a localStorage write, whose `storage` event likewise
 * only fires in OTHER windows. Either way the sending window stays put.
 *
 * Every window calls `listen()` once at startup (see app/main.js) so the marked
 * tab can act as the receiver; the sender calls `openCustomer()` / `send()`.
 */
define('secondScreenBridge',[
  'app'
], function (App) {
  'use strict';

  var CHANNEL_NAME = 'compuzz-second-screen';
  var LS_KEY = 'compuzz.secondScreen.nav';
  // Per-tab identity (sessionStorage: unique per tab, survives that tab's
  // reloads) and the shared "which tab is the target" pointer (localStorage:
  // visible to every tab, survives reloads). A tab is the target when its
  // sessionStorage id equals the localStorage target id.
  var WINDOW_ID_KEY = 'compuzz.secondScreen.windowId';
  var TARGET_ID_KEY = 'compuzz.secondScreen.targetId';

  // Lazily-created singleton channel for this window. Kept as a single
  // instance because BroadcastChannel does not echo a message back to the
  // instance that sent it — so reusing one instance for both send and receive
  // guarantees the sender never navigates itself.
  var channel = null;
  function getChannel() {
    if (channel === null && typeof window.BroadcastChannel === 'function') {
      try { channel = new window.BroadcastChannel(CHANNEL_NAME); }
      catch (e) { channel = false; }
    }
    return channel || null;
  }

  function genId() {
    return 'w-' + new Date().getTime() + '-' + Math.random().toString(36).slice(2, 8);
  }

  // This tab's stable id. sessionStorage keeps it constant across reloads of
  // the same tab; if it's unavailable we fall back to an in-memory id (which
  // simply resets on reload — the rep would re-mark the tab).
  var memId = null;
  function windowId() {
    try {
      var id = window.sessionStorage.getItem(WINDOW_ID_KEY);
      if (!id) { id = genId(); window.sessionStorage.setItem(WINDOW_ID_KEY, id); }
      return id;
    } catch (e) {
      if (!memId) { memId = genId(); }
      return memId;
    }
  }

  function readTargetId() {
    try { return window.localStorage.getItem(TARGET_ID_KEY) || null; }
    catch (e) { return null; }
  }

  // Navigate THIS window to a route pushed from another window — but only when
  // this tab is the marked target. We deliberately do not call window.focus():
  // the rep keeps the board screen in view and the inbox screen focused; the
  // board just updates in place.
  function navigateHere(route) {
    if (route && SecondScreen.isTarget()) { App.navigate(route, { trigger: true }); }
  }

  var SecondScreen = {

    // Push a route fragment to the dashboard's other open windows.
    send: function (route) {
      if (!route) { return; }
      var bc = getChannel();
      if (bc) {
        bc.postMessage({ route: route, t: new Date().getTime() });
        return;
      }
      // No BroadcastChannel — a localStorage write fires `storage` in the
      // other windows. The timestamp guarantees the value always changes, so
      // pushing the same route twice still triggers the event.
      try {
        window.localStorage.setItem(LS_KEY, JSON.stringify({ route: route, t: new Date().getTime() }));
      } catch (e) { /* localStorage disabled — nothing we can do */ }
    },

    // Map a customer (by end-user code) + a target to a route and push it.
    // Targets: 'profile' / 'offersList' / 'ordersList' (customer-detail tabs)
    // and 'offersBoard' / 'ordersBoard' (board, filtered to the customer).
    // Returns false when no customer is linked or the target is unknown.
    openCustomer: function (target, endUserId) {
      if (!endUserId) { return false; }
      var route;
      switch (target) {
        case 'profile':  route = 'customers/code/' + endUserId + '/profile'; break;
        case 'offersList':  route = 'customers/code/' + endUserId + '/offers'; break;
        case 'ordersList': route = 'customers/code/' + endUserId + '/orders'; break;
        case 'offersBoard': route = 'offers_kanban/filter/' + endUserId; break;
        case 'ordersBoard': route = 'orders_kanban/filter/' + endUserId; break;
        default: return false;
      }
      SecondScreen.send(route);
      return true;
    },

    // Open a company file on the second screen (by company secId). Mirrors the
    // in-app "#companies/:secId" link the company card exposes. Returns false
    // without a secId.
    openCompany: function (companySecId) {
      if (!companySecId) { return false; }
      SecondScreen.send('companies/' + encodeURIComponent(companySecId));
      return true;
    },

    // Open a specific offer/order (by purchaseOrderId) on the second screen.
    // The orders detail route dispatches by command type, so the same route
    // opens either an offer or an order. Returns false without a poi.
    openCommand: function (poi) {
      if (!poi) { return false; }
      SecondScreen.send('orders/' + encodeURIComponent(poi));
      return true;
    },

    // Open the customers list on the second screen. Used as the fallback for
    // the "profile" shortcut when an inbox item has no linked customer yet —
    // the rep can find or create the customer on the board screen.
    openCustomersList: function () {
      SecondScreen.send('customers');
      return true;
    },

    // --- Targeting -------------------------------------------------------

    // Is THIS tab the marked second-screen target?
    isTarget: function () {
      var t = readTargetId();
      return !!t && t === windowId();
    },

    // Is ANY tab currently marked as the target? (true even if that tab was
    // since closed — we can't cheaply detect a dead target.)
    hasTarget: function () {
      return !!readTargetId();
    },

    // Mark this tab as the target (replacing any previous one).
    markAsTarget: function () {
      try { window.localStorage.setItem(TARGET_ID_KEY, windowId()); }
      catch (e) { /* storage disabled — targeting can't persist */ }
      App.trigger('secondScreen:targetChanged', true);
    },

    // Stop this tab being the target. Only clears the pointer when it actually
    // points at us, so we never yank the target out from under another tab.
    clearTarget: function () {
      if (readTargetId() === windowId()) {
        try { window.localStorage.removeItem(TARGET_ID_KEY); }
        catch (e) { /* no-op */ }
      }
      App.trigger('secondScreen:targetChanged', false);
    },

    // Toggle this tab's target state; returns the new state.
    toggleTarget: function () {
      if (SecondScreen.isTarget()) { SecondScreen.clearTarget(); return false; }
      SecondScreen.markAsTarget();
      return true;
    },

    // Register this window as a receiver. Idempotent — safe to call once at
    // startup in every window.
    listen: function () {
      if (SecondScreen._listening) { return; }
      SecondScreen._listening = true;

      var bc = getChannel();
      if (bc) {
        bc.onmessage = function (ev) {
          if (ev && ev.data && ev.data.route) { navigateHere(ev.data.route); }
        };
      }

      window.addEventListener('storage', function (ev) {
        if (!ev) { return; }
        // Another tab claimed/released the target — refresh our header toggle.
        if (ev.key === TARGET_ID_KEY) {
          App.trigger('secondScreen:targetChanged', SecondScreen.isTarget());
          return;
        }
        // A pushed route via the localStorage fallback (covers browsers without
        // BroadcastChannel; harmless otherwise since we never write LS_KEY when
        // BroadcastChannel is active).
        if (ev.key === LS_KEY && ev.newValue) {
          try {
            var msg = JSON.parse(ev.newValue);
            if (msg && msg.route) { navigateHere(msg.route); }
          } catch (e) { /* ignore malformed payloads */ }
        }
      });
    }
  };

  return SecondScreen;
});

